import { createApp } from 'vue';

// --- Vuetify ---
import 'vuetify/styles';
import { createVuetify } from 'vuetify';
import * as components from 'vuetify/components';
import * as directives from 'vuetify/directives';
import { aliases, mdi } from 'vuetify/iconsets/mdi';
import '@mdi/font/css/materialdesignicons.css';

import PrimeVue from 'primevue/config';
import App from './App.vue';
import router from './router';
import store from '../store';

import { createPinia } from 'pinia';
import VCalendar from 'v-calendar';
import DayJsAdapter from '@date-io/dayjs';
import { INTERACTIVE_STATE_TOKENS } from '@/shared/ui/interactiveStateTokens';
import 'v-calendar/style.css';
import { createHead } from '@vueuse/head';
import { emitToast } from './shared/lib/toast';
import './styles/tokens.css';
import './styles/dialogs.css';
import './services/httpClient';
import { loadConferenceConfig } from '@/shared/conferenceConfig';
// Side-import all admin feature registries so registerAdminUI() runs at
// startup and per-domain detailHeader slots are wired into the generic
// admin framework. Registry calls are cheap and inert when no admin
// detail page is open. See: admin/features/index.ts.
import '@/admin/features';

// Перехват нативных alert -> наши toast-уведомления
(window as any).alert = (message?: string) => {
    emitToast(String(message ?? ''), 'info');
};

// Создаём Vuetify-инстанс
const vuetify = createVuetify({
    components,
    directives,
    icons: {
        defaultSet: 'mdi',
        aliases,
        sets: { mdi },
    },
    theme: {
        defaultTheme: 'light',
        themes: {
            light: {
                colors: {
                    primary:    '#7B65F7',
                    secondary:  '#8162E9',
                    accent:     '#C8C2FB',
                    error:      '#DC2626',
                    warning:    '#D97706',
                    info:       '#2563EB',
                    success:    '#16a34a',
                    background: '#F8FAFC',
                    surface:    '#FFFFFF',
                },
                // Interactive-state tokens (UX 16.4 / 9.1) — single source of
                // truth is shared/ui/interactiveStateTokens.ts. Vuetify emits
                // each key as `--v-{key}` (e.g. --v-app-state-disabled-opacity)
                // so platform components share the exact same disabled / busy
                // treatment. The canonical un-prefixed `--app-state-*` aliases
                // that AppButton.vue consumes are mirrored in styles/tokens.css.
                variables: {
                    'app-state-disabled-opacity': INTERACTIVE_STATE_TOKENS.disabled.opacity,
                    'app-state-disabled-cursor':  INTERACTIVE_STATE_TOKENS.disabled.cursor,
                    'app-state-busy-opacity':     INTERACTIVE_STATE_TOKENS.loading.opacity,
                    'app-state-busy-cursor':      INTERACTIVE_STATE_TOKENS.loading.cursor,
                },
            },
        },
    },
    date: {
        adapter: DayJsAdapter,
    },
});

// Создаём pinia-инстанс
const pinia = createPinia();
const head = createHead();

// --------------------------------------------------
// One-shot legacy localStorage cleanup
// --------------------------------------------------
// MentorWelcomeWizard.vue was deleted 2026-05-28 (R5 of the onboarding
// shell refactor). Its three localStorage keys — mentor_wizard_done,
// mentor_wizard_step, mentor_wizard_payment_chosen — were stored without
// a per-user namespace (one of the leaks tracked in the #3 audit), so
// any session that ever ran the wizard left them behind. New code does
// not write them; we sweep here so legacy users don't keep them
// indefinitely. A sentinel flag prevents the sweep from re-running every
// boot. F-#3's centralised logout-sweep will remove this once it lands.
const WIZARD_LEGACY_SWEEP_FLAG = 'logora_mentor_wizard_legacy_sweep_v1';
try {
    if (
        typeof localStorage !== 'undefined'
        && !localStorage.getItem(WIZARD_LEGACY_SWEEP_FLAG)
    ) {
        for (const key of [
            'mentor_wizard_done',
            'mentor_wizard_step',
            'mentor_wizard_payment_chosen',
        ]) {
            localStorage.removeItem(key);
        }
        localStorage.setItem(WIZARD_LEGACY_SWEEP_FLAG, '1');
    }
} catch {
    /* private mode / quota — non-critical, the file just stays cleaner next boot */
}

// --------------------------------------------------
// 2) Асинхронная инициализация приложения
// --------------------------------------------------
// После deploy'а dynamic-imported chunk hashes меняются.  Если у браузера
// stale main.js / router chunk-manifest, fetch chunk'а по старому hash
// вернёт 404 → ChunkLoadError → страница падает в пустоту.  Делаем один
// hard-reload, чтобы браузер подтянул свежий index.html и пересобрал
// ссылки на chunk'и.  sessionStorage-флаг защищает от infinite-loop:
// если после reload chunk всё ещё отсутствует (deploy реально сломан),
// второй раз не перезагружаем — иначе бесконечный цикл.
const CHUNK_RELOAD_FLAG = 'logora_chunk_reload_attempted';
const CHUNK_ERROR_PATTERNS = /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i;
router.onError((error: unknown, to) => {
    const message = String((error as { message?: string })?.message ?? '');
    if (!CHUNK_ERROR_PATTERNS.test(message)) {
        return;
    }
    let alreadyReloaded = false;
    try {
        alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1';
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
    } catch {
        // sessionStorage может быть запрещён (incognito + 3rd-party context);
        // тогда просто не сможем защититься от loop'а — но это редкий кейс.
    }
    if (alreadyReloaded) {
        // Reload не помог — скорее всего браузер выдаёт stale 404 из
        // disk-cache (закешировал с `Cache-Control: immutable` когда-то
        // в окно с broken nginx config).  Сервер сейчас отдаёт файл OK,
        // лечится только manual cache clear или incognito.  Показываем
        // баннер с этой инструкцией.
        console.error(
            '[chunk-load] reload уже пробовали — браузер выдаёт stale 404 из кеша',
            { error, attemptedPath: to?.fullPath },
        );
        const existing = document.getElementById('logora-stale-cache-banner');
        if (!existing) {
            const banner = document.createElement('div');
            banner.id = 'logora-stale-cache-banner';
            banner.style.cssText = [
                'position:fixed', 'top:0', 'left:0', 'right:0',
                'background:#fff3cd', 'color:#664d03',
                'border-bottom:1px solid #ffecb5',
                'padding:14px 20px', 'z-index:99999',
                'font-family:Tilda Sans,sans-serif', 'font-size:14px',
                'line-height:1.5', 'box-shadow:0 2px 8px rgba(0,0,0,0.08)',
            ].join(';');
            banner.innerHTML = `
                <strong>Не удалось загрузить часть страницы.</strong>
                Скорее всего ваш браузер закешировал старую версию.
                <a href="#" onclick="event.preventDefault();
                    (async () => {
                        try {
                            if (window.caches && caches.keys) {
                                const keys = await caches.keys();
                                await Promise.all(keys.map(k =&gt; caches.delete(k)));
                            }
                        } catch (e) {}
                        try { sessionStorage.clear(); } catch (e) {}
                        window.location.reload();
                    })();" style="color:#664d03;text-decoration:underline;font-weight:600;">
                    Очистить кеш и перезагрузить
                </a>
                · или откройте сайт в режиме инкогнито.
            `;
            document.body.appendChild(banner);
        }
        return;
    }
    // location.replace вместо assign — не плодим лишнюю history-запись,
    // чтобы Back-кнопка не возвращала пользователя в сломанное состояние.
    window.location.replace(to?.fullPath ?? window.location.href);
});

// Сбрасываем флаг при успешной навигации — следующий ChunkLoadError снова
// получит право на одну попытку recovery.
router.afterEach(() => {
    try {
        sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    } catch {
        /* sessionStorage недоступен — игнор */
    }
});

async function initializeApp() {
    try {
        // Узнаём, авторизован ли пользователь (endpoint /users/me/)
        await store.dispatch('fetchUser');
    } catch (error) {
        console.warn('Не удалось получить пользователя при старте:', error);
    }

    // Runtime-конфиг конференций (окно подключения) из backend, ДО монтирования —
    // дальше joinWindowMinutes() везде вернёт актуальное значение. Не бросает
    // (внутри try/catch), на дефолт 15 фолбэчится сам.
    await loadConferenceConfig();

    const app = createApp(App);
    app.config.compilerOptions.isCustomElement = (tag) => tag === 'video-player-container';
    app.use(vuetify);
    app.use(router);
    app.use(store);
    app.use(head);
    app.use(PrimeVue);
    app.use(VCalendar, {});
    app.use(pinia);
    app.mount('#app');
}

initializeApp();
